import React, { useState } from 'react';
import { useOktaAuth } from '@okta/okta-react';
import { Container, Card, Row, Col, Alert, Button, Badge, Spinner } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faChartBar,
  faSyncAlt,
  faShieldAlt,
  faCheckCircle,
  faHeartbeat,
  faLock,
  faStar,
  faEye
} from '@fortawesome/free-solid-svg-icons';
import AwsChatbot from '../AwsChatbot';
import AndroidAppFlavours from '../shared/AndroidAppFlavours';
import packageJson from '../../../package.json';

const mockHealthCards = [
  {
    id: 'dsv-card-01',
    title: 'Autonomic HRV & Readiness',
    source: 'Oura / Health Connect',
    value: '68 ms',
    trend: '+4 ms vs 7-day avg',
    status: 'Optimal',
    variant: 'success'
  },
  {
    id: 'dsv-card-02',
    title: 'Continuous Glucose Waveform',
    source: 'Dexcom CGM Feed',
    value: '98 mg/dL',
    trend: 'In Range (96%)',
    status: 'Stable',
    variant: 'info'
  },
  {
    id: 'dsv-card-03',
    title: 'Daily Sleep Architecture',
    source: 'Huawei Health / Samsung Health',
    value: '7h 42m',
    trend: 'REM: 1h 50m | Deep: 1h 35m',
    status: 'Restored',
    variant: 'warning'
  },
  {
    id: 'dsv-card-04',
    title: 'Step Cadence & Caloric Expenditure',
    source: 'HealthDash Mobile Android',
    value: '11,420 steps',
    trend: '620 kcal burned',
    status: 'Goal Met',
    variant: 'success'
  }
];

const DSVPage = () => {
  const { authState } = useOktaAuth();
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [refreshMessage, setRefreshMessage] = useState('');
  const [showFullPreview, setShowFullPreview] = useState(false);

  const isAuthenticated = Boolean(authState?.isAuthenticated);
  // Premium subscription check from token claims
  const isPremiumUser = Boolean(authState?.idToken?.claims?.subscription === 'Premium');

  const handleRefresh = async () => {
    setIsRefreshing(true);
    setRefreshMessage('Requesting on-demand data refresh from connected telemetry sources...');
    try {
      await new Promise(resolve => setTimeout(resolve, 1500));
      setRefreshMessage('Success! The health cards have been queued and synchronized.');
    } catch (error) {
      console.error('Failed to refresh DSV data:', error);
      setRefreshMessage('An error occurred during synchronization. Please try again.');
    } finally {
      setIsRefreshing(false);
    }
  };

  return (
    <Container className="ecosystem-page py-5 mt-4">
      <header className="border-bottom pb-3 mb-4">
        <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
          <div>
            <div className="d-flex align-items-center gap-2 mb-1">
              <h1 className="font-weight-bold mb-0">Data Source Visualizer (<abbr title="Data Source Visualizer">DSV</abbr>)</h1>
              <Badge bg="warning" text="dark" className="fw-bold">Analytics Engine</Badge>
            </div>
            <p className="text-muted mb-0">
              Cross-platform visualizer and telemetry aggregator for digital health records. (v{packageJson.version})
            </p>
          </div>
          <div>
            {isPremiumUser ? (
              <Badge bg="success" className="p-2 fs-6">
                <FontAwesomeIcon icon={faStar} className="me-1" />
                Premium Active
              </Badge>
            ) : (
              <Badge bg="secondary" className="p-2 fs-6">
                {isAuthenticated ? 'Standard Account' : 'Visitor Preview'}
              </Badge>
            )}
          </div>
        </div>
      </header>

      {/* Visualizer Telemetry Cards Grid */}
      <div className="mb-4">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h5 className="font-weight-bold mb-0">
            <FontAwesomeIcon icon={faChartBar} className="me-2 text-warning" />
            Visualized Health Telemetry Records
          </h5>
          {!isPremiumUser && (
            <Button
              variant="outline-warning"
              size="sm"
              className="fw-bold"
              onClick={() => setShowFullPreview(prev => !prev)}
            >
              <FontAwesomeIcon icon={showFullPreview ? faLock : faEye} className="me-1" />
              {showFullPreview ? 'Enable Privacy Filter' : 'Inspect Sample Telemetry'}
            </Button>
          )}
        </div>

        <div className="position-relative">
          <Row className={`g-3 ${!isPremiumUser && !showFullPreview ? 'user-select-none' : ''}`} style={!isPremiumUser && !showFullPreview ? { filter: 'blur(3px)', opacity: 0.75 } : {}}>
            {mockHealthCards.map(card => (
              <Col md={6} lg={3} key={card.id}>
                <Card className="h-100 shadow-sm border-0 rounded-3 p-3">
                  <div className="d-flex justify-content-between align-items-start mb-2">
                    <span className="badge bg-light text-dark border small">{card.source}</span>
                    <Badge bg={card.variant}>{card.status}</Badge>
                  </div>
                  <h6 className="font-weight-bold mb-1">{card.title}</h6>
                  <div className="text-muted small mb-2"><code>{card.id}</code></div>
                  <div className="display-7 fw-bold text-dark mb-2">{card.value}</div>
                  <div className="small text-muted mt-auto border-top pt-2">
                    <FontAwesomeIcon icon={faHeartbeat} className="me-1 text-danger" />
                    {card.trend}
                  </div>
                </Card>
              </Col>
            ))}
          </Row>

          {!isPremiumUser && !showFullPreview && (
            <div
              className="position-absolute top-50 start-50 translate-middle text-center p-4 rounded-3 shadow-lg"
              style={{
                backgroundColor: 'rgba(43, 55, 59, 0.92)',
                color: '#ffffff',
                maxWidth: '480px',
                width: '90%',
                border: '1px solid rgba(247, 148, 0, 0.5)',
                zIndex: 5
              }}
            >
              <FontAwesomeIcon icon={faLock} className="text-warning display-5 mb-3" />
              <h5 className="font-weight-bold mb-2">High-Resolution Visualizer Locked</h5>
              <p className="small mb-3 text-light">
                Sign in with a <strong>Personalized Cloud Dashboard (PCD)</strong> account or activate sample preview to inspect aggregated health telemetry charts.
              </p>
              <div className="d-flex justify-content-center gap-2">
                <Button
                  variant="warning"
                  size="sm"
                  className="fw-bold text-white shadow-sm"
                  onClick={() => setShowFullPreview(true)}
                >
                  <FontAwesomeIcon icon={faEye} className="me-1" />
                  Inspect Sample Telemetry
                </Button>
                {!isAuthenticated && (
                  <Link to="/login" className="btn btn-outline-light btn-sm fw-bold">
                    Sign In
                  </Link>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Premium On-Demand Refresh Feature */}
      <Card className="border-0 shadow-sm rounded-3 mb-4 p-4 bg-light">
        <div className="d-flex justify-content-between align-items-center flex-wrap gap-3">
          <div>
            <h5 className="font-weight-bold mb-1">
              <FontAwesomeIcon icon={faSyncAlt} className="me-2 text-warning" />
              On-Demand Health Card Refresh
            </h5>
            <p className="text-muted small mb-0">
              Synchronizes real-time metrics across all connected wearable vendors and queues immediate record processing.
            </p>
          </div>
          <div>
            {isPremiumUser ? (
              <Button
                variant="warning"
                className="fw-bold text-white shadow-sm"
                onClick={handleRefresh}
                disabled={isRefreshing}
              >
                {isRefreshing ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-2" />
                    Refreshing...
                  </>
                ) : (
                  <>
                    <FontAwesomeIcon icon={faSyncAlt} className="me-2" />
                    Refresh Health Cards
                  </>
                )}
              </Button>
            ) : (
              <Button
                variant="outline-secondary"
                disabled
                className="fw-bold"
                title="Available exclusively for Premium PCD users"
              >
                <FontAwesomeIcon icon={faLock} className="me-2" />
                Premium Only
              </Button>
            )}
          </div>
        </div>

        {refreshMessage && (
          <Alert variant="info" className="mt-3 mb-0 py-2 px-3 small border-0 shadow-sm">
            <FontAwesomeIcon icon={faCheckCircle} className="me-2" />
            {refreshMessage}
          </Alert>
        )}

        {!isPremiumUser && (
          <Alert variant="warning" className="mt-3 mb-0 py-2 px-3 small border-0">
            <FontAwesomeIcon icon={faShieldAlt} className="me-2" />
            The on-demand refresh feature is available exclusively for Premium and Enterprise PCD accounts.
          </Alert>
        )}
      </Card>

      {/* Android Companion Flavours */}
      <Card className="border-0 shadow-sm rounded-3 mb-4 p-3 bg-light">
        <div className="d-flex justify-content-between align-items-center mb-1">
          <span className="fw-bold small text-secondary">Android Companion Applications</span>
          <span className="text-muted small">DSV Compatible</span>
        </div>
        <AndroidAppFlavours defaultOpen={false} />
      </Card>

      <AwsChatbot />
    </Container>
  );
};

export default DSVPage;